// src/components/DetailsModal/DetailsModal.jsx
import React, { useMemo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import "./DetailsModal.scss";
import InputField from "../InputComponent/InputField";
import Toast from "../Toast/Toast";
import { normalizeRole, ROLES } from "../../config/roles";
import TooltipPortal from "../TooltipPortal/TooltipPortal";
import DeleteButton from "../DeleteButton/DeleteButton";
// NOTE: removed axiosInstace import because parent supplies projectMembers prop

// helpers
const addDays = (yyyyMmDd, n) => {
  try {
    if (!yyyyMmDd) return "";
    const d = new Date(yyyyMmDd);
    if (Number.isNaN(d)) return "";
    const out = new Date(d);
    out.setDate(out.getDate() + Number(n || 0));
    return out.toISOString().slice(0, 10);
  } catch {
    return "";
  }
};
const todayYMD = () => new Date().toISOString().slice(0, 10);

export default function DetailsModal({
  item,
  currentUserRole,
  projectEndDate,
  onClose = () => {},
  onSave = async () => {},
  currentEmpId,
  currentUserId = null,
  parentTaskOwnerId = null,
  onDeleteTask,
  onDeleteTodoList,
  onUpdateTodoList,
  projectMembers = [], // parent provides this
}) {
  const safe = item ?? {};
  const isTask = !!safe.taskId;
  const isTodo = !!safe.toDoId;
  const isChecklist = !!(safe.toDoListId || safe.toDoListTitle);

  const [closing, setClosing] = useState(false);

  // Toast local state
  const [toast, setToast] = useState({
    message: "",
    type: "info",
    visible: false,
  });

  const showToast = (message, type = "info") => {
    setToast({ message, type, visible: true });
  };

  const closeToast = () => {
    setToast((prev) => ({ ...prev, visible: false }));
  };

  // scheduling mode detection — only relevant for task mode
  const initialSchedulingMode = useMemo(() => {
    if (!isTask) return "DATE";
    const hasDates =
      safe.startDate &&
      String(safe.startDate).length >= 8 &&
      safe.endDate &&
      String(safe.endDate).length >= 8;
    if (safe.schedulingMode === "DAY" || safe.durationDays != null)
      return "DAY";
    if (safe.schedulingMode === "DATE" || hasDates) return "DATE";
    return "DATE";
  }, [
    isTask,
    safe.startDate,
    safe.endDate,
    safe.schedulingMode,
    safe.durationDays,
  ]);

  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState(initialSchedulingMode);

  const [selectedMember, setSelectedMember] = useState(null);

  const [formData, setFormData] = useState(() => ({
    // shared fields
    title: safe.taskTitle || safe.toDoTitle || safe.toDoListTitle || "",

    description: safe.description || safe.taskDesc || "",

    remark: safe.remark || "",

    priority: safe.priority || "MEDIUM",

    // scheduling fields (task only)
    startDate: (safe.startDate || "").split("T")[0] || "",
    endDate:
      initialSchedulingMode === "DATE"
        ? (safe.endDate || "").split("T")[0] || ""
        : "",
    durationDays:
      initialSchedulingMode === "DAY"
        ? Number.isFinite(Number(safe.durationDays))
          ? Number(safe.durationDays)
          : 0
        : 0,
  }));
  // console.log("DM init formData:", formData); // Removed excessive log
  const role = normalizeRole(currentUserRole);
  const isCreatorOrApprover = role === ROLES.CREATOR || role === ROLES.APPROVER;

  // ----------------------------------------------------------------------
  // broadened owner-field detection — check the item itself, then its parent (todo), then parent task
  const todoOwnerIdFromItem =
    safe.assignedEmpId ??
    safe.assignedToEmpId ??
    safe.assignedTo ??
    safe.assigneeEmpId ??
    safe.assigneeId ??
    safe.empId ??
    safe.userId ??
    safe.assigned ??
    // CHECK PARENT TODO (for checklist items passed with __parent = todo)
    (safe.__parent &&
      (safe.__parent.assignedEmpId ??
        safe.__parent.assignedToEmpId ??
        safe.__parent.assignedTo ??
        safe.__parent.assigneeEmpId ??
        safe.__parent.assigneeId ??
        safe.__parent.empId ??
        safe.__parent.userId ??
        null)) ??
    null;

  // parent task owner (may live on safe or on the parent todo's parent task)
  const taskOwnerIdFromItem = safe.taskOwnerId ?? safe.ownerId;
  const parentTaskOwnerIdFromItem =
    safe.parentTaskOwnerId ??
    // if checklist was opened with __parent = todo, that todo may carry taskOwnerId
    (safe.__parent && (safe.__parent.taskOwnerId ?? safe.__parent.ownerId)) ??
    null;

  // isTodoOwner: allow match from either currentUserId OR currentEmpId against the todo owner we found
  const isTodoOwnerCheck1 =
    currentUserId != null &&
    todoOwnerIdFromItem != null &&
    String(currentUserId) === String(todoOwnerIdFromItem);
  const isTodoOwnerCheck2 =
    currentEmpId != null &&
    todoOwnerIdFromItem != null &&
    String(currentEmpId) === String(todoOwnerIdFromItem);
  const isTodoOwner = isTodoOwnerCheck1 || isTodoOwnerCheck2;

  // ----------------------------------------------------------------------
  useEffect(() => {
    if (!item) return;
    setTimeout(() => {
      const el = document.getElementById("dm7865-title");
      el?.focus();
    }, 0);
  }, [item]);

  useEffect(() => {
    if (!item) return;
    document.body.classList.add("modal-open");
    return () => {
      document.body.classList.remove("modal-open");
    };
  }, [item]);

  const ownerId = isTask ? taskOwnerIdFromItem : parentTaskOwnerIdFromItem;

  const isTaskOwner =
    currentUserId != null &&
    ownerId != null &&
    String(currentUserId) === String(ownerId);

  // permission booleans (unchanged)
  const canEditTask =
    isCreatorOrApprover || (role === ROLES.DOER && isTaskOwner);

  // Final robust logic for canEditTodo
  const canEditTodo =
    isCreatorOrApprover || // Highest roles can always edit
    isTaskOwner || // Task Owner can edit all To-Dos
    (role === ROLES.DOER && isTodoOwner); // DOER can edit their own To-Do

  const canEdit = isTask ? canEditTask : canEditTodo;

  const handleField = (name, value) =>
    setFormData((prev) => ({ ...prev, [name]: value }));

  const effectiveEndDate =
    mode === "DATE"
      ? formData.endDate || ""
      : formData.startDate
        ? addDays(formData.startDate, formData.durationDays || 0)
        : "";

  // validation: adjust per-mode
  const validateBeforeSave = () => {
    // 1) Common: title required
    if (!formData.title || String(formData.title).trim() === "") {
      return { ok: false, error: "Please enter a title." };
    }

    // 2) Checklist: only title matters — skip task/date checks
    if (isChecklist) {
      return { ok: true };
    }

    // 3) To-Do: title (and maybe remark) — no date checks
    if (isTodo) {
      return { ok: true };
    }

    // 4) Task-related checks
    if (!formData.startDate) {
      return { ok: false, error: "Please select a start date." };
    }
    if (mode === "DATE" && !formData.endDate) {
      return { ok: false, error: "Please select an end date." };
    }
    if (
      mode === "DAY" &&
      (!Number.isFinite(Number(formData.durationDays)) ||
        Number(formData.durationDays) < 0)
    ) {
      return { ok: false, error: "Duration (days) must be 0 or greater." };
    }
    if (
      effectiveEndDate &&
      formData.startDate &&
      effectiveEndDate < formData.startDate
    ) {
      return {
        ok: false,
        error: "End date cannot be earlier than start date.",
      };
    }
    if (
      projectEndDate &&
      effectiveEndDate &&
      effectiveEndDate > projectEndDate
    ) {
      return {
        ok: false,
        error: `Task cannot end after the project end date (${projectEndDate}).`,
      };
    }
    const today = todayYMD();
    if (effectiveEndDate && effectiveEndDate < today) {
      return { ok: true, warn: "The selected end date is in the past." };
    }
    return { ok: true };
  };

  const resolveAccessId = (sel) => {
    if (!sel) return safe.accessId ?? null;
    if (sel.accessId != null) return sel.accessId;
    // try to find in projectMembers by empId to get their accessId
    const found = projectMembers.find(
      (m) => Number(m.empId) === Number(sel.empId),
    );
    if (found && found.accessId != null) return found.accessId;
    return safe.accessId ?? null;
  };
  // Filter out Viewer / Client AND sort alphabetically
  const sortedMembers = useMemo(() => {
    const list = Array.isArray(projectMembers) ? projectMembers : [];

    return [...list]
      .filter((m) => {
        const role = (m.roleType || "").toLowerCase();
        return role !== "viewer" && role !== "client";
      })
      .sort((a, b) => (a.empName || "").localeCompare(b.empName || ""));
  }, [projectMembers]);

  // Resolve assignedEmpId robustly (prefer selectedMember.empId, fallback to safe)
  const resolveAssignedEmpId = (sel) => {
    if (!sel) return safe.assignedEmpId ?? null;
    if (sel.empId != null) return sel.empId;
    // fallback to any found member's empId
    const found = projectMembers.find(
      (m) => m.accessId != null && m.accessId === sel.accessId,
    );
    return found ? found.empId : (safe.assignedEmpId ?? null);
  };

  const handleSave = async () => {
    if (!canEdit) return;

    const check = validateBeforeSave();
    if (!check.ok) {
      showToast(check.error, "error");
      return;
    }
    if (check.warn) {
      showToast(check.warn, "info");
    }

    // Build outbound payload depending on what we're editing
    let outbound;
    if (isChecklist) {
      // Checklist (ToDoList) expects only the title
      outbound = {
        toDoListTitle: formData.title,
      };
    } else if (isTodo) {
      // ToDo expects toDoTitle and remark — include assignment fields
      outbound = {
        toDoTitle: formData.title,
        remark: formData.remark,
        accessId: resolveAccessId(selectedMember),
        assignedEmpId: resolveAssignedEmpId(selectedMember),
      };
    } else {
      // Task payload (form-level keys: parent will map to API fields)
      outbound = {
        title: formData.title,
        description: formData.description,
        remark: formData.remark,
        priority: formData.priority,
        startDate: formData.startDate || null,
        endDate: mode === "DATE" ? formData.endDate : effectiveEndDate || null,
        durationDays:
          mode === "DAY" ? Number(formData.durationDays || 0) : undefined,
        schedulingMode: mode,
        accessId: resolveAccessId(selectedMember),
        assignedEmpId: resolveAssignedEmpId(selectedMember),
      };

      // If task has nested todos and you want to update their assignment too:
      if (safe.todoRequestDTOList && safe.todoRequestDTOList.length > 0) {
        outbound.todoRequestDTOList = safe.todoRequestDTOList.map((todo) => ({
          ...todo,
          accessId: resolveAccessId(selectedMember) ?? todo.accessId ?? null,
          assignedEmpId:
            resolveAssignedEmpId(selectedMember) ?? todo.assignedEmpId ?? null,
        }));
      }
    }

    setSaving(true);
    try {
      console.log("DETAILS OUTBOUND => calling onSave:", {
        taskId: safe.taskId ?? safe.toDoId ?? safe.toDoListId ?? null,
        outbound,
        selectedMember,
      });

      await onSave(safe, outbound);
      console.log("OUTBOUND before onSave:", outbound);

      showToast("Changes saved successfully!", "success");
      onClose();
    } catch (err) {
      console.error("Failed to save details:", err);
      showToast("Failed to save changes.", "error");
    } finally {
      setSaving(false);
    }
  };
  // Resolve an accessId for a selectedMember (fall back to projectMembers lookup / safe / null)

  // -------------------- MEMBERS SELECTION LOGIC (use parent prop) --------------------

  // initialize selectedMember when projectMembers or item change
  useEffect(() => {
    // If no members provided, fallback to item-assigned or null
    if (!Array.isArray(projectMembers) || projectMembers.length === 0) {
      const assignedEmpId = safe.assignedEmpId ?? safe.empId ?? 0;
      if (assignedEmpId) {
        setSelectedMember({
          accessId: null,
          empId: Number(assignedEmpId),
          empName: safe.assignedEmpName ?? `#${assignedEmpId}`,
        });
      } else {
        setSelectedMember(null);
      }
      return;
    }

    // prefer accessId on item
    const acc = safe.accessId ?? null;
    const assignedEmpId = safe.assignedEmpId ?? safe.empId ?? null;

    const matchByAccess =
      acc != null
        ? projectMembers.find((m) => String(m.accessId) === String(acc))
        : null;
    const matchByEmp =
      assignedEmpId != null
        ? projectMembers.find((m) => String(m.empId) === String(assignedEmpId))
        : null;

    if (matchByAccess) {
      setSelectedMember(matchByAccess);
      return;
    }
    if (matchByEmp) {
      setSelectedMember(matchByEmp);
      return;
    }

    // if nothing assigned, but current user is a project member, preselect them
    if (currentEmpId) {
      const me = projectMembers.find(
        (m) => String(m.empId) === String(currentEmpId),
      );
      if (me) {
        setSelectedMember(me);
        return;
      }
    }

    // otherwise unassigned
    setSelectedMember(null);
  }, [
    projectMembers,
    item,
    currentEmpId,
    safe.accessId,
    safe.assignedEmpId,
    safe.empId,
    safe.assignedEmpName,
  ]);

  // -------------------- END MEMBERS LOGIC --------------------

  if (!item) return null;

  return (
    <>
      <div className="modal7865-overlay" onClick={onClose}>
        <div
          className={`modal7865-content ${isChecklist ? "is-checklist" : ""} ${
            closing ? "closing" : ""
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal7865-header">
            <h3 className="modal7865-title">
              {isTask
                ? "Task Details"
                : isTodo
                  ? "Task Details"
                  : "Checklist Details"}
            </h3>
            <button
              type="button"
              className="modal7865-close-btn"
              aria-label="Close"
              onClick={() => {
                setClosing(true);
                setTimeout(() => onClose(), 280);
              }}
            >
              ✕
            </button>
          </div>
          {!canEdit && (
            <div className="modal7865-readonly">
              Read-only — you don’t have permission to edit this{" "}
              {isTask ? "task" : "to-do"}.
            </div>
          )}
          {/* Only show project end banner for tasks (optional) */}
          {isTask && projectEndDate && (
            <div className="modal7865-project-end">
              Project ends on <strong>{projectEndDate}</strong>. Your task must
              end on or before this date.
            </div>
          )}
          {/* For tasks: allow scheduling mode. For todos: skip entirely */}
          {isTask && (
            <div className="modal7865-row" style={{ marginBottom: 6 }}>
              <div
                className="modal7865-pill-toggle"
                role="tablist"
                aria-label="Scheduling mode"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === "DATE"}
                  className={`pill ${mode === "DATE" ? "active" : ""}`}
                  onClick={() => {
                    if (!canEdit) return;
                    setMode("DATE");
                    setFormData((prev) => ({
                      ...prev,
                      endDate:
                        prev.endDate ||
                        (safe.endDate ? safe.endDate.split("T")[0] : ""),
                      durationDays: 0,
                    }));
                  }}
                  disabled={!canEdit}
                >
                  Date (Start & End)
                </button>

                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === "DAY"}
                  className={`pill ${mode === "DAY" ? "active" : ""}`}
                  onClick={() => {
                    if (!canEdit) return;
                    setMode("DAY");
                    setFormData((prev) => ({
                      ...prev,
                      endDate: "",
                      durationDays:
                        prev.durationDays ||
                        (Number.isFinite(Number(safe.durationDays))
                          ? Number(safe.durationDays)
                          : 0),
                    }));
                  }}
                  disabled={!canEdit}
                >
                  Duration (Days)
                </button>
              </div>
            </div>
          )}
          <form className="modal7865-form" onSubmit={(e) => e.preventDefault()}>
            {/* Title - shared */}
            <div className="dm-title-priority-row">
              {/* 60% Title */}
              <div className="dm-title-col">
                <InputField
                  label={isTodo ? "Task Title" : "Title"}
                  id="dm7865-title"
                  value={formData.title}
                  onChange={(e) => handleField("title", e.target.value)}
                  disabled={!canEdit}
                />
              </div>

              {/* 40% Priority Pills */}
              {isTask && (
                <div className="dm-priority-col">
                  <label className="dm-priority-label">Priority</label>

                  <div className="dm-priority-pill-group">
                    {["LOW", "MEDIUM", "HIGH"].map((p) => (
                      <div
                        key={p}
                        className={`dm-priority-pill 
              ${p.toLowerCase()} 
              ${formData.priority === p ? "selected" : ""}`}
                        onClick={() => canEdit && handleField("priority", p)}
                      >
                        {p[0] + p.slice(1).toLowerCase()}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {/* TASK: Description + Remark */}
            {isTask && (
              <div className="dm-desc-remark-row">
                <div className="dm-desc-col">
                  <InputField
                    label="Description"
                    as="textarea"
                    value={formData.description}
                    onChange={(e) => handleField("description", e.target.value)}
                    disabled={!canEdit}
                  />
                </div>

                <div className="dm-remark-col">
                  <InputField
                    label="Remark"
                    as="textarea"
                    value={formData.remark}
                    onChange={(e) => handleField("remark", e.target.value)}
                    disabled={!canEdit}
                  />
                </div>
              </div>
            )}

            {/* TODO: Remark only */}
            {isTodo && (
              <div className="dm-remark-only">
                <InputField
                  label="Remark"
                  as="textarea"
                  value={formData.remark}
                  onChange={(e) => handleField("remark", e.target.value)}
                  disabled={!canEdit}
                />
              </div>
            )}

            {/* Description only for tasks */}

            {/* Member dropdown (native select) */}

            {/* Remark — hide when checklist */}

            {/* Priority only for tasks */}

            {/* Dates / Duration only for tasks */}
            {isTask && (
              <div
                className={`modal7865-dates ${
                  mode === "DATE" ? "mode-date" : "mode-day"
                }`}
              >
                <InputField
                  label="Start Date"
                  id="dm7865-start"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleField("startDate", e.target.value)}
                  disabled={!canEdit}
                />

                {mode === "DATE" ? (
                  <InputField
                    label="End Date"
                    id="dm7865-end"
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => handleField("endDate", e.target.value)}
                    disabled={!canEdit}
                  />
                ) : (
                  <InputField
                    label="Duration (days)"
                    id="dm7865-duration"
                    type="number"
                    min={0}
                    step={1}
                    value={formData.durationDays}
                    onChange={(e) =>
                      handleField(
                        "durationDays",
                        e.target.value.replace(/[^\d-]/g, ""),
                      )
                    }
                    disabled={!canEdit}
                  />
                )}
              </div>
            )}

            {/* For task: hint about computed end date when in DAY mode */}
            {isTask && mode === "DAY" && formData.startDate && (
              <div className="modal7865-hint">
                Ends on <strong>{effectiveEndDate || "—"}</strong>
                {projectEndDate &&
                effectiveEndDate &&
                effectiveEndDate > projectEndDate
                  ? " (exceeds project end!)"
                  : ""}
              </div>
            )}
            {(isTask || isTodo) && (
              <div className="modal7865-row" style={{ marginTop: 8 }}>
                <label
                  htmlFor="dm-member-select"
                  style={{ display: "block", fontSize: 13, marginBottom: 6 }}
                >
                  Assign
                </label>
                <div className="dm-assign-wrapper">
                  <div className="dm-assign-pills">
                    {/* Members */}

                    {sortedMembers.map((m) => (
                      <div
                        key={m.empId}
                        className={`dm-assign-pill ${
                          selectedMember?.empId === m.empId ? "selected" : ""
                        }`}
                        onClick={() => canEdit && setSelectedMember(m)}
                      >
                        {m.empName}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </form>
          <div className="modal7865-actions">
            {/* LEFT — Delete for checklist only */}
            {isChecklist && typeof onDeleteTodoList === "function" && (
              <div className="delete-zone">
                <TooltipPortal
                  label="Delete this Checklist (cannot be undone)"
                  offsetY={28}
                >
                  <DeleteButton
                    onClick={async () => {
                      setSaving(true);
                      try {
                        await onDeleteTodoList(safe.toDoListId ?? safe.id);
                        showToast("Checklist deleted.", "success");
                        onClose(); // ✅ close after successful delete
                      } catch (err) {
                        console.error("Delete checklist failed:", err);
                        showToast("Failed to delete checklist.", "error");
                      } finally {
                        setSaving(false);
                      }
                    }}
                    aria-label="Delete checklist"
                  />
                </TooltipPortal>
              </div>
            )}

            {/* RIGHT — Close / Save */}
            <div className="action-zone">
              <button
                type="button"
                className="btn-cancel7865"
                disabled={saving}
                onClick={() => {
                  setClosing(true);
                  setTimeout(() => {
                    onClose();
                  }, 280); // must match CSS duration
                }}
              >
                Close
              </button>

              {canEdit && (
                <button
                  type="button"
                  className="btn-save7865"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast.visible && (
        <Toast message={toast.message} type={toast.type} onClose={closeToast} />
      )}
    </>
  );
}

DetailsModal.propTypes = {
  item: PropTypes.object,
  currentUserRole: PropTypes.any,
  projectEndDate: PropTypes.string,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  currentEmpId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  parentTaskOwnerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onDeleteTask: PropTypes.func,
  onDeleteTodoList: PropTypes.func,
  onUpdateTodoList: PropTypes.func,
  projectMembers: PropTypes.array,
};
